// Photo Discovery Utility
// Helps discover and manage photos uploaded directly to S3
import React from 'react';
import { getImageUrl, getThumbnailUrl, getOptimizedUrl } from './imageConfig';

/**
 * Discovers photos in S3 gallery directories
 * @param {string} galleryName - Name of the gallery (e.g., 'wedding')
 * @returns {Promise<Array>} Array of photo objects
 */
export const discoverGalleryPhotos = async (galleryName) => {
  try {
    // For client-side discovery, we'll use a manifest approach
    // This requires a manifest.json file to be uploaded alongside photos
    const manifestPath = `/images/galleries/${galleryName}/manifest.json`;
    
    // In development, try local manifest first, fallback to production
    let manifestUrl = manifestPath; // Local first
    if (process.env.NODE_ENV === 'development') {
      console.log(`Development mode: Trying local manifest first: ${manifestUrl}`);
    } else {
      manifestUrl = getImageUrl(manifestPath);
    }
    
    console.log(`Fetching manifest from: ${manifestUrl}`);
    
    let response = await fetch(manifestUrl);
    
    // If local manifest fails in development, try production
    if (!response.ok && process.env.NODE_ENV === 'development') {
      const productionManifestUrl = getImageUrl(manifestPath);
      console.log(`Local manifest failed, trying production: ${productionManifestUrl}`);
      response = await fetch(productionManifestUrl);
    }
    
    if (!response.ok) {
      console.warn(`No manifest found for ${galleryName} gallery at ${manifestUrl}`);
      return [];
    }
    
    const manifest = await response.json();
    
    // Transform manifest data to gallery format with proper URLs
    return manifest.photos.map(photo => ({
      src: getImageUrl(`/images/galleries/${galleryName}/${photo.filename}`),
      thumbnailSrc: getImageUrl(`/images/galleries/${galleryName}/thumbnails/${photo.filename}`),
      optimizedSrc: getImageUrl(`/images/galleries/${galleryName}/optimized/${photo.filename}`),
      alt: photo.alt || photo.filename,
      caption: photo.caption || photo.filename,
      uploadDate: photo.uploadDate || null,
      metadata: photo.metadata || {},
      isDiscovered: true // Flag to indicate this was auto-discovered
    }));
    
  } catch (error) {
    console.error(`Error discovering photos for ${galleryName}:`, error);
    return [];
  }
};

/**
 * Merges discovered photos with static gallery data
 * @param {Array} staticPhotos - Photos from galleryData.js
 * @param {Array} discoveredPhotos - Photos discovered from S3
 * @returns {Array} Merged photo array with environment-aware URLs
 */
export const mergeGalleryPhotos = (staticPhotos = [], discoveredPhotos = []) => {
  // Convert static photos to use environment-aware URLs
  const processedStaticPhotos = staticPhotos.map(photo => ({
    ...photo,
    src: getImageUrl(photo.src),
    thumbnailSrc: getImageUrl(getThumbnailUrl(photo.src)),
    optimizedSrc: getImageUrl(getOptimizedUrl(photo.src))
  }));
  
  const merged = [...processedStaticPhotos];
  
  // Add discovered photos that aren't already in static data
  // Compare by filename instead of full URL
  discoveredPhotos.forEach(discoveredPhoto => {
    const discoveredFilename = discoveredPhoto.src.split('/').pop();
    
    const exists = staticPhotos.some(staticPhoto => {
      const staticFilename = staticPhoto.src.split('/').pop();
      return staticFilename === discoveredFilename;
    });
    
    if (!exists) {
      merged.push(discoveredPhoto);
    }
  });
  
  return merged;
};

/**
 * Generates a manifest.json template for a gallery
 * @param {Array} filenames - Array of photo filenames
 * @returns {Object} Manifest object
 */
export const generateManifestTemplate = (filenames) => {
  return {
    gallery: "wedding",
    lastUpdated: new Date().toISOString(),
    photos: filenames.map(filename => ({
      filename: filename,
      alt: `Wedding photo - ${filename}`,
      caption: `Wedding photo - ${filename}`,
      uploadDate: new Date().toISOString(),
      metadata: {
        photographer: "",
        location: "",
        date: "",
        people: []
      }
    }))
  };
};

/**
 * Validates photo file extensions
 * @param {string} filename - Photo filename
 * @returns {boolean} True if valid photo extension
 */
export const isValidPhotoFile = (filename) => {
  const validExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp'];
  const extension = filename.toLowerCase().substring(filename.lastIndexOf('.'));
  return validExtensions.includes(extension);
};

/**
 * Hook for using gallery photos with discovery
 * @param {string} galleryName - Name of the gallery
 * @param {Array} staticPhotos - Static photos from galleryData.js
 * @returns {Object} { photos, loading, error, refresh }
 */
export const useGalleryPhotos = (galleryName, staticPhotos = []) => {
  const [photos, setPhotos] = React.useState(staticPhotos);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  
  const loadPhotos = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const discoveredPhotos = await discoverGalleryPhotos(galleryName);
      const mergedPhotos = mergeGalleryPhotos(staticPhotos, discoveredPhotos);
      setPhotos(mergedPhotos);
    } catch (err) {
      setError(err.message);
      setPhotos(staticPhotos); // Fallback to static photos
    } finally {
      setLoading(false);
    }
  }, [galleryName, staticPhotos]);
  
  React.useEffect(() => {
    loadPhotos();
  }, [loadPhotos]);
  
  return {
    photos,
    loading,
    error,
    refresh: loadPhotos
  };
};

// Example manifest.json structure for wedding gallery:
/*
{
  "gallery": "wedding",
  "lastUpdated": "2024-12-19T18:00:00Z",
  "photos": [
    {
      "filename": "marco_demarco.jpg",
      "alt": "Marco de Marco",
      "caption": "Ciao",
      "uploadDate": "2024-12-19T18:00:00Z",
      "metadata": {
        "photographer": "Family",
        "location": "Southington, CT",
        "date": "1974-12-06",
        "people": ["Marco De Marco"]
      }
    },
    {
      "filename": "GiuseppinaRossetti_PasqualeDeMarco.jpg",
      "alt": "Giuseppina Rossetti & Pasquale DeMarco",
      "caption": "Newlyweds - 1969",
      "uploadDate": "2024-12-19T18:00:00Z",
      "metadata": {
        "photographer": "Wedding Photographer",
        "location": "Italy",
        "date": "1969",
        "people": ["Giuseppina Rossetti", "Pasquale DeMarco"]
      }
    }
  ]
}
*/